import { DOCUMENT, NgIf } from '@angular/common';
import {
	Component,
	Inject,
	OnDestroy,
	OnInit,
	ViewEncapsulation,
} from '@angular/core';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { filter, Subject, takeUntil } from 'rxjs';

import { ThemeConfigService } from '../core/config/theme/theme.config.service';
import { ThemeConfig } from '../core/config/theme/theme.config.types';

import { UltramarineLayout } from './components/ultramarine-layout/ultramarine-layout.component';
import { Layout } from './layout.types';

@Component({
	selector: 'layout',
	templateUrl: './layout.component.html',
	styleUrls: ['./layout.component.scss'],
	encapsulation: ViewEncapsulation.None,
	imports: [NgIf, UltramarineLayout],
})
export class LayoutComponent implements OnInit, OnDestroy {
	config: ThemeConfig;
	layout: Layout;
	scheme: 'dark' | 'light';
	theme: string;
	private _unsubscribeAll: Subject<any> = new Subject<any>();

	constructor(
		@Inject(DOCUMENT) private _document: any,
		private _activatedRoute: ActivatedRoute,
		private _router: Router,
		private _ThemeConfigService: ThemeConfigService,
	) {}

	ngOnInit(): void {
		this._ThemeConfigService.config$
			.pipe(takeUntil(this._unsubscribeAll))
			.subscribe((config: ThemeConfig) => {
				this.config = config;
				this._updateLayout();
			});
		this._router.events
			.pipe(
				filter((event) => event instanceof NavigationEnd),
				takeUntil(this._unsubscribeAll),
			)
			.subscribe(() => {
				this._updateLayout();
			});
	}

	private _updateLayout(): void {
		// Get the current activated route
		let route = this._activatedRoute;
		while (route.firstChild) {
			route = route.firstChild;
		}

		// 1. Set the layout from the config
		this.layout = this.config.layout;

		// 2. Get the query parameter from the current route and
		// set the layout and save the layout to the config
		const layoutFromQueryParam = route.snapshot.queryParamMap.get(
			'layout',
		) as Layout;
		if (layoutFromQueryParam) {
			this.layout = layoutFromQueryParam;
			if (this.config) {
				this.config.layout = layoutFromQueryParam;
			}
		}

		// 3. Iterate through the paths and change the layout as we find
		// a config for it.
		//
		// The reason we do this is that there might be empty grouping
		// paths or component-less routes along the path. Because of that,
		// we cannot just assume that the layout configuration will be
		// in the last path's config or in the first path's config.
		//
		// So, we get all the paths that matched starting from root all
		// the way to the current activated route, walk through them one
		// by one and change the layout as we find the layout config. This
		// way, layout configuration can live anywhere within the path and
		// we won't miss it.
		//
		// Also, this will allow overriding the layout in any time so we
		// can have different layouts for different routes.
		const paths = route.pathFromRoot;
		paths.forEach((path) => {
			// Check if there is a 'layout' data
			if (
				path.routeConfig &&
				path.routeConfig.data &&
				path.routeConfig.data.layout
			) {
				// Set the layout
				this.layout = path.routeConfig.data.layout;
			}
		});
	}

	ngOnDestroy(): void {
		this._unsubscribeAll.next(null);
		this._unsubscribeAll.complete();
	}
}
